import React from "react";
import toast from "react-hot-toast";
import { useAuth } from "react-oidc-context";
import { useNavigate, useParams } from "react-router-dom";
import { ConfirmModal } from "../../../components/modals";
import { toNString } from "../../../helpers/guids";
import { useDeleteClientSetMutation, useFetchClientSetDetailsQuery, useFetchClientSetsListQuery, useJoinClientSetsMutation } from "../../../services/campaignmanagement";
import { ClientSetPermissionsModal, CreateClientSetModal, CreateSetProjectModal, ExportContactDetailsModal, SelectClientSetModal } from "../components";

const ClientSetContext = React.createContext();

export const ClientSetEditModes = {
    Union: { value: 0, label: "Borrow clients from" },
    Intersection: { value: 1, label: "Retain clients from" },
    Except: { value: 2, label: "Remove clients of" }
}

export const useClientSetContext = () => React.useContext(ClientSetContext);

export const ClientSetProvider = ({ children }) => {

    const navigate = useNavigate();
    const { clientSetId: selectedSetId } = useParams();

    // Need to handle the optional segment in the url AND deleting (and thus deselecting) a set
    const setSelectedSetId = (id) => {
        if (selectedSetId != null) {
            // There is something already in the URL, so we need to remove it and replace it with the new ID/empty string if deleting
            navigate(`../${id ?? ""}`, { replace: true, relative: 'path' });
        } else
            navigate(id ?? "", { replace: true });
    }

    React.useEffect(() => {
        let sessionSetId = sessionStorage.getItem("clientSetId");

        // If there is a setId in the URL, update session storage
        if (selectedSetId != null) {
            if (sessionSetId !== selectedSetId)
                sessionStorage.setItem("clientSetId", selectedSetId);
            return;
        } else if (sessionSetId != null)
            // If there is something in session but not in URL, set the URL to the session value
            setSelectedSetId(sessionSetId);
    }, [selectedSetId]);

    const { data: selectedSet, isFetching: isFetchingSet, isError: fetchSetIsError, refetch: refetchSet } = useFetchClientSetDetailsQuery({ clientSetId: selectedSetId }, { skip: !selectedSetId });
    const { data: clientSets, ...fetchResult } = useFetchClientSetsListQuery();

    // For determining allowed set options
    const { readAccessIds, writeAccessIds } = selectedSet || { readAccessIds: [], writeAccessIds: [] };
    const auth = useAuth();
    const { user: { profile: { sub, roles } } } = auth;
    const userId = toNString(sub);
    const canRead = readAccessIds.includes(userId) || roles.includes("sys_admin_access");
    const canWrite = writeAccessIds.includes(userId) || roles.includes("sys_admin_access");

    // Basic set options
    const [showCreateModal, setShowCreateModal] = React.useState(false);
    const [showOpenModal, setShowOpenModal] = React.useState(false);

    // Edit modals will either allow joining two sets (borrow/remove/retain) or adding/removing master accounts to/from a set
    const [showEditModal, setShowEditModal] = React.useState(false);
    const [editMode, setEditMode] = React.useState(null);

    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = React.useState(false);
    const [showPermissionsModal, setShowPermissionsModal] = React.useState(false);
    const [showCreateProjectsModal, setShowCreateProjectsModal] = React.useState(false);
    const [showExportModal, setShowExportModal] = React.useState(false);
    const [isEmailExport, setIsEmailExport] = React.useState(false);

    // Offcanvas options
    const clientSetOptions = React.useMemo(() => [
        {
            label: <><strong>Create</strong> New Set</>,
            onClick: () => setShowCreateModal(true)
        },
        {
            label: <><strong>Open</strong> Existing Set</>,
            onClick: () => setShowOpenModal(true)
        },
        {
            label: <><strong>Delete</strong> This Set</>,
            onClick: () => setShowConfirmDeleteModal(true),
            hidden: selectedSetId == null || !canWrite
        },
        {
            label: <><strong>Set Access</strong> Permissions</>,
            onClick: () => setShowPermissionsModal(true),
            hidden: selectedSetId == null || !canRead
        },
        {
            label: <><strong>Borrow Accounts</strong> from Another Set</>,
            onClick: () => {
                setShowEditModal(true);
                setEditMode(ClientSetEditModes.Union);
            },
            hidden: selectedSetId == null || !canWrite
        },
        {
            label: <><strong>Remove All Members</strong> of Another Set</>,
            onClick: () => {
                setShowEditModal(true);
                setEditMode(ClientSetEditModes.Except);
            },
            hidden: selectedSetId == null || !canWrite
        },
        {
            label: <><strong>Retain Only Members</strong> of Another Set</>,
            onClick: () => {
                setShowEditModal(true);
                setEditMode(ClientSetEditModes.Intersection);
            },
            hidden: selectedSetId == null || !canWrite
        },
        {
            label: <><strong>Create Client Projects</strong> from This Set</>,
            onClick: () => setShowCreateProjectsModal(true),
            hidden: selectedSetId == null || !canRead
        },
        {
            label: <><strong>Export</strong> Contact Details to File</>,
            onClick: () => {
                setShowExportModal(true);
                setIsEmailExport(false);
            },
            hidden: selectedSetId == null || !canRead
        },
        {
            label: <><strong>Export</strong> Email Recipients to File</>,
            onClick: () => {
                setShowExportModal(true);
                setIsEmailExport(true);
            },
            hidden: selectedSetId == null || !canRead
        }
    ], [canRead, canWrite, selectedSetId]);

    const [showSetOptions, setShowSetOptions] = React.useState(false);

    // Show options menu when no set selected
    React.useEffect(() => {
        setShowSetOptions(selectedSetId == null);
    }, [selectedSetId]);

    const [deleteTrigger, { isLoading: isDeleting }] = useDeleteClientSetMutation();
    const deleteClientSet = () => {
        if (!selectedSetId)
            return;

        return toast.promise(deleteTrigger({ clientSetId: selectedSetId }).unwrap(), {
            loading: "Deleting client set...",
            success: () => {
                setShowConfirmDeleteModal(false);
                setSelectedSetId(null);
                sessionStorage.removeItem("clientSetId");
                return "Client set deleted successfully!";
            },
            error: (err) => {
                setShowConfirmDeleteModal(false);
                console.error(err);
                return err?.data?.message ?? err?.data ?? "Failed to delete client set.";
            }
        });
    }

    // Clear master accounts after set edit operations
    const [shouldClear, setShouldClear] = React.useState(false);

    const [joinTrigger, { isLoading: isJoining }] = useJoinClientSetsMutation();
    const joinSets = (joinSetId) => {
        return toast.promise(joinTrigger({
            clientSetId: selectedSetId,
            joinBehaviour: editMode.value,
            joinSetId
        }).unwrap(), {
            loading: "Updating client set...",
            success: () => {
                // Close the options menu
                setShowSetOptions(false);

                // Trigger a re-fetch of the client set master accounts
                setShouldClear(true);
                setTimeout(() => setShouldClear(false), 0);

                return "Client set updated successfully!";
            },
            error: (err) => {
                console.error(err);
                return err?.data?.message ?? err?.data ?? "Failed to update client set.";
            }
        }).finally(() => {
            // Close the modal
            setShowEditModal(false);
            setEditMode(null);
        });
    }

    // Master account ref/name search
    const [searchTerm, setSearchTerm] = React.useState(null);

    // Reset when switching sets
    React.useEffect(() => {
        setSearchTerm(null);
    }, [selectedSetId]);

    return <ClientSetContext.Provider value={{
        selectedSetId,
        selectedSet,
        isFetchingSet,
        fetchSetIsError,
        refetchSet,
        showSetOptions,
        setShowSetOptions,
        clientSetOptions,
        isJoining,
        isDeleting,
        clientSets,
        joinSets,
        searchTerm,
        setSearchTerm,
        shouldClear,
        ...fetchResult
    }}>
        <CreateClientSetModal
            show={showCreateModal}
            handleClose={(newSetId = null) => {
                if (newSetId)
                    setSelectedSetId(newSetId);

                setShowCreateModal(false)
            }}
        />
        {/* Open Set */}
        <SelectClientSetModal
            show={showOpenModal}
            onHide={() => setShowOpenModal(false)}
            onSelect={(value) => {
                setSelectedSetId(value);
                setShowOpenModal(false);
            }}
        />
        {/* Edit Set Modal */}
        <SelectClientSetModal
            show={showEditModal}
            onHide={() => {
                setShowEditModal(false);
                setEditMode(null);
            }}
            editMode={editMode}
            onConfirm={joinSets}
        />
        {/* Remove set modal */}
        <ConfirmModal
            show={showConfirmDeleteModal}
            dialogTitle="Are you sure?"
            message="Are you sure you want to delete this client set? This action cannot be undone."
            confirmButtonText="Delete Set"
            cancelButtonText="Cancel"
            confirmButtonVariant="danger"
            proceed={deleteClientSet}
            cancel={() => setShowConfirmDeleteModal(false)}
        />
        <ClientSetPermissionsModal
            show={showPermissionsModal}
            onHide={() => setShowPermissionsModal(false)}
        />
        <CreateSetProjectModal
            show={showCreateProjectsModal}
            onHide={() => setShowCreateProjectsModal(false)}
        />
        <ExportContactDetailsModal
            isEmailExport={isEmailExport}
            show={showExportModal}
            onHide={() => setShowExportModal(false)}
        />
        {children}
    </ClientSetContext.Provider>
}